import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ListView } from '../../design-system/list-view/ListView'
import { RowActionsMenu } from '../../design-system/list-view/RowActionsMenu'
import type { ActiveFilterChip } from '../../design-system/list-view/FilterPanel'
import type { Density, ExportFormat, SortSpec } from '../../design-system/list-view/types'
import { usePersistentState } from '../../lib/usePersistentState'
import { useDebouncedValue } from '../../lib/useDebouncedValue'
import { useSavedViews } from '../../lib/savedViews'
import { rsqlAnd, rsqlEquals, rsqlSearch } from '../../lib/rsql'
import { runExport, exportCapNote, type ExportColumn } from '../../lib/export'
import { listContacts, patchContact } from '../../api/contacts'
import { ApiError } from '../../api/problem'
import type { ContactView } from '../../types/contact'
import { AccountLookupField } from '../accounts/AccountLookupField'
import { CONTACT_COLUMN_ORDER, buildContactColumns } from './columns'
import { ContactFormModal } from './ContactFormModal'

const PAGE_LIMIT = 50

interface FacetState {
  account: { id: string; name: string } | null
}

function emptyFacets(): FacetState {
  return { account: null }
}

function buildFilter(facets: FacetState, search: string): string | null {
  const accountClause = facets.account ? rsqlEquals('accountId', facets.account.id) : null
  const searchClause = rsqlSearch(['name', 'email', 'title'], search)
  return rsqlAnd(accountClause, searchClause) || null
}

function buildSort(sort: SortSpec[]): string | null {
  if (sort.length === 0) return null
  return sort.map((s) => (s.direction === 'desc' ? `-${s.columnId}` : s.columnId)).join(',')
}

/** Everything a saved view needs to capture for this list (Saved Views/Filters story). `FacetState`
 * is already JSON-serializable, so it is captured verbatim. */
interface ContactsSavedViewState {
  facets: FacetState
  search: string
  sort: SortSpec[]
  columnOrder: string[]
  hiddenColumnIds: string[]
  pinnedColumnIds: string[]
  density: Density
}

/**
 * Contacts (IB-48 AC14) — replaces the `/clients/contacts` `ComingSoonStub`.
 * Columns carry the Account parent per CLAUDE.md §4's cross-list rule (the
 * master-data subset: Contact shows its Account, no Case/Fiscal year — those
 * only apply to case-scoped lists). Row-click now navigates to the real
 * `ContactDetailScreen` (`/clients/contacts/:id`, CRM completeness — IB-93),
 * mirroring `AccountsListScreen`; `ContactFormModal` remains available as a
 * distinct "Edit" row action for the fast in-place path.
 */
export function ContactsListScreen() {
  const navigate = useNavigate()

  const [contacts, setContacts] = useState<ContactView[]>([])

  /**
   * IB-27 S3 — the `title` retrofit target (design §4c). `commitContactTitle` is the ONLY write
   * path this column ever calls: the existing capability-gated `PATCH /contacts/{id}`, never
   * `AdjustmentService` (`title` is not a guarded field). Rejection propagates as-is — `ApiError`
   * is already an `Error` whose `.message` is the server's exact `problem.detail` (AC5); `DataGrid`
   * shows it inline without this screen needing to unwrap it.
   */
  const commitContactTitle = useCallback(async (row: ContactView, next: string) => {
    const trimmed = next.trim()
    const { contact } = await patchContact(row.id, { title: trimmed === '' ? null : trimmed }, row.version)
    setContacts((prev) => prev.map((c) => (c.id === contact.id ? contact : c)))
  }, [])

  const columns = useMemo(() => buildContactColumns({ onEditTitle: commitContactTitle }), [commitContactTitle])

  const [columnOrder, setColumnOrder] = usePersistentState<string[]>('iw.contacts.columnOrder', CONTACT_COLUMN_ORDER)
  const [hiddenColumnIdsArr, setHiddenColumnIdsArr] = usePersistentState<string[]>(
    'iw.contacts.hiddenColumns',
    columns.filter((c) => c.defaultHidden).map((c) => c.id),
  )
  const [pinnedColumnIdsArr, setPinnedColumnIdsArr] = usePersistentState<string[]>('iw.contacts.pinnedColumns', ['name'])
  const [columnWidths, setColumnWidths] = usePersistentState<Record<string, number>>('iw.contacts.columnWidths', {})
  const [density, setDensity] = usePersistentState<Density>('iw.contacts.density', 'compact')

  const hiddenColumnIds = useMemo(() => new Set(hiddenColumnIdsArr), [hiddenColumnIdsArr])
  const pinnedColumnIds = useMemo(() => new Set(pinnedColumnIdsArr), [pinnedColumnIdsArr])

  const [facets, setFacets] = useState<FacetState>(emptyFacets)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const [sort, setSort] = useState<SortSpec[]>([{ columnId: 'name', direction: 'asc' }])
  const [selection, setSelection] = useState<Set<string>>(new Set())

  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; contact: ContactView } | null>(null)

  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filterString = useMemo(() => buildFilter(facets, debouncedSearch), [facets, debouncedSearch])
  const sortString = useMemo(() => buildSort(sort), [sort])
  const latestRequestRef = useRef(0)

  const fetchPage = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      const requestId = ++latestRequestRef.current
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        } else {
          setLoadingMore(true)
        }
        const page = await listContacts({ filter: filterString, sort: sortString, cursor: afterCursor, limit: PAGE_LIMIT })
        if (latestRequestRef.current !== requestId) return
        setContacts((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load contacts. Check your connection and try again.')
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [filterString, sortString],
  )

  useEffect(() => {
    setSelection(new Set())
    void fetchPage(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString, sortString])

  function retry() {
    void fetchPage(null, true)
  }

  const activeFilterChips: ActiveFilterChip[] = useMemo(() => {
    const chips: ActiveFilterChip[] = []
    if (facets.account) chips.push({ id: 'account', label: `Account: ${facets.account.name}` })
    return chips
  }, [facets])

  function removeFilterChip(id: string) {
    if (id === 'account') setFacets((prev) => ({ ...prev, account: null }))
  }

  function clearAllFilters() {
    setFacets(emptyFacets())
    setSearch('')
  }

  const hasAnyFilterOrSearch = Boolean(facets.account) || debouncedSearch.trim() !== ''

  function toggleColumnHidden(id: string) {
    setHiddenColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  function moveColumn(id: string, direction: -1 | 1) {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const tmp = next[index]!
      next[index] = next[target]!
      next[target] = tmp
      return next
    })
  }
  function toggleColumnPin(id: string) {
    setPinnedColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  /** IB-27 S3 — persisted per-user resize widths (`iw.contacts.columnWidths`, design §4b). */
  function handleColumnWidthChange(id: string, width: number) {
    setColumnWidths((prev) => ({ ...prev, [id]: width }))
  }

  const savedViewState: ContactsSavedViewState = useMemo(
    () => ({ facets, search, sort, columnOrder, hiddenColumnIds: hiddenColumnIdsArr, pinnedColumnIds: pinnedColumnIdsArr, density }),
    [facets, search, sort, columnOrder, hiddenColumnIdsArr, pinnedColumnIdsArr, density],
  )
  const applySavedViewState = useCallback((state: ContactsSavedViewState) => {
    setFacets(state.facets)
    setSearch(state.search)
    setSort(state.sort)
    setColumnOrder(state.columnOrder)
    setHiddenColumnIdsArr(state.hiddenColumnIds)
    setPinnedColumnIdsArr(state.pinnedColumnIds)
    setDensity(state.density)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const savedViews = useSavedViews<ContactsSavedViewState>('contacts', savedViewState, applySavedViewState)

  const exportColumns: ExportColumn<ContactView>[] = useMemo(
    () =>
      columnOrder
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined && !hiddenColumnIds.has(c.id))
        .map((c) => ({ id: c.id, header: c.header, toText: c.toExportText })),
    [columnOrder, hiddenColumnIds, columns],
  )

  function handleExport(format: ExportFormat) {
    runExport(format, 'Contacts', contacts, exportColumns)
  }
  function handleExportSelected(selectedIds: string[]) {
    const idSet = new Set(selectedIds)
    runExport('csv', 'Contacts (selected)', contacts.filter((c) => idSet.has(c.id)), exportColumns)
  }

  const state: 'loading' | 'error' | 'empty-tenant' | 'empty-filtered' | 'ready' = error
    ? 'error'
    : loading
      ? 'loading'
      : contacts.length === 0
        ? hasAnyFilterOrSearch
          ? 'empty-filtered'
          : 'empty-tenant'
        : 'ready'

  return (
    <>
      <ListView<ContactView>
        entityIcon="users"
        title="Contacts"
        countLabel={loading ? undefined : `${contacts.length}${cursor ? '+' : ''}`}
        primaryAction={{ label: 'New contact', icon: 'plus', onClick: () => setModal({ mode: 'create' }) }}
        quickFilters={[]}
        activeQuickFilterIds={new Set()}
        onQuickFilterToggle={() => {}}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search contacts"
        searchPlaceholder="Search by name, email, title…"
        filterChildren={<AccountFacet facets={facets} setFacets={setFacets} />}
        activeFilterCount={activeFilterChips.length}
        activeFilterChips={activeFilterChips}
        onRemoveFilterChip={removeFilterChip}
        onClearFilters={clearAllFilters}
        savedViews={savedViews}
        onExport={handleExport}
        exportNote={exportCapNote(contacts.length, Boolean(cursor))}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumnIds={hiddenColumnIds}
        pinnedColumnIds={pinnedColumnIds}
        onToggleColumnHidden={toggleColumnHidden}
        onMoveColumn={moveColumn}
        onToggleColumnPin={toggleColumnPin}
        columnWidths={columnWidths}
        onColumnWidthChange={handleColumnWidthChange}
        sort={sort}
        onSortChange={setSort}
        density={density}
        onDensityChange={setDensity}
        state={state}
        rows={contacts}
        getRowId={(row) => row.id}
        onRowActivate={(row) => navigate(`/clients/contacts/${row.id}`)}
        renderRowActions={(row) => (
          <RowActionsMenu
            label={`Actions for ${row.name}`}
            actions={[
              { id: 'open', label: 'Open', icon: 'chevron-right', onSelect: () => navigate(`/clients/contacts/${row.id}`) },
              { id: 'edit', label: 'Edit', icon: 'edit-3', onSelect: () => setModal({ mode: 'edit', contact: row }) },
            ]}
          />
        )}
        selection={selection}
        onSelectionChange={setSelection}
        bulkActions={[{ id: 'export-selected', label: 'Export selected', icon: 'download', onRun: handleExportSelected }]}
        errorMessage={error ?? undefined}
        onRetry={retry}
        emptyTenant={{
          icon: 'users',
          title: 'No contacts yet',
          description: 'Create the first contact for an account to get started.',
          action: (
            <button type="button" className="iw-btn iw-btn--primary" onClick={() => setModal({ mode: 'create' })}>
              New contact
            </button>
          ),
        }}
        hasMore={Boolean(cursor)}
        onLoadMore={() => void fetchPage(cursor, false)}
        loadingMore={loadingMore}
      />

      {modal?.mode === 'create' ? (
        <ContactFormModal
          mode="create"
          onClose={() => setModal(null)}
          onSaved={(contact) => {
            setModal(null)
            setContacts((prev) => [contact, ...prev])
          }}
        />
      ) : null}
      {modal?.mode === 'edit' ? (
        <ContactFormModal
          mode="edit"
          editing={{ contact: modal.contact, version: modal.contact.version }}
          onClose={() => setModal(null)}
          onSaved={(contact) => {
            setModal(null)
            setContacts((prev) => prev.map((c) => (c.id === contact.id ? contact : c)))
          }}
        />
      ) : null}
    </>
  )
}

interface AccountFacetProps {
  facets: FacetState
  setFacets: (updater: (prev: FacetState) => FacetState) => void
}

/** The Account facet inside the FilterPanel — filters by `accountId` (CLAUDE.md §4 cross-list rule). */
function AccountFacet({ facets, setFacets }: AccountFacetProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <AccountLookupField
        id="contacts-facet-account"
        label="Account"
        value={facets.account}
        onChange={(next) => setFacets((prev) => ({ ...prev, account: next }))}
      />
    </div>
  )
}
